import { CommonModule, TitleCasePipe } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { routerReducer } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { FormlyModule } from '@ngx-formly/core';
import { FormlySelectModule } from '@ngx-formly/core/select';
import {
  UppyAngularDashboardModalModule,
  UppyAngularDashboardModule,
  UppyAngularDragDropModule,
  UppyAngularProgressBarModule,
  UppyAngularStatusBarModule,
} from '@uppy/angular';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { NgChartsModule } from 'ng2-charts';
import { MarkdownModule } from 'ngx-markdown';
import {
  CategoryEffects,
  ClickOutsideDirective,
  DefaultImageDirective,
  ErrorEffects,
  FormlyComponents,
  FormlySetup,
  MessagesComponent,
  PricePipe,
  ThkeeCommonModule,
  TooltipDirective,
  TopicEffects,
  UiSnippetsComponent,
  categoryFeature,
  errorFeature,
  topicFeature,
} from 'thkee-common';
import {
  AccordionComponent,
  AdminHeaderComponent,
  AiButtonComponent,
  AssignmentsAccordionComponent,
  BreadcurmbsComponent,
  CarouselComponent,
  CarouselNavComponent,
  CategoryHeaderComponent,
  ChartComponent,
  CommonSwiperComponent,
  CourseCardComponent,
  CourseGridComponent,
  CourseHeaderComponent,
  CourseSidebarComponent,
  FeedbackComponent,
  FilterOptionComponent,
  FirebaseAuthComponent,
  FormLoginComponent,
  FormSignupComponent,
  FormTapPaymentComponent,
  FormlyDeleteConfirmationComponent,
  FormlyFileDashboardInfoComponent,
  FormlyFileDefaultInfoComponent,
  FormlyFileDragDropInfoComponent,
  FormlyFileVideoPlayerComponent,
  FormlyImgPlaceholderComponent,
  FormlyRepeatButtonComponent,
  GoogleAuthComponent,
  HeroOneComponent,
  LearninglabArticleComponent,
  LearninglabAssignmentComponent,
  LearninglabHeaderComponent,
  LearninglabQuizComponent,
  LearninglabSidebarComponent,
  LearninglabVideoComponent,
  MainEditorComponent,
  ModalComponent,
  ModalConfirmComponent,
  ModalLoginComponent,
  NavigationComponent,
  NewCourseHeaderComponent,
  PaginationComponent,
  ProgressBarComponent,
  ProgressCircleComponent,
  QaACardComponent,
  QaClassCardComponent,
  QaQCardComponent,
  QaTabMenuComponent,
  ScrollableContentComponent,
  SocialShareButtonComponent,
  SpinnerComponent,
  StarRatingComponent,
  StudentHeaderComponent,
  SvgComponent,
  TabMenuComponent,
  ThkVideoPlayerComponent,
  ToastComponent,
} from './components';
import {
  CheckoutEffects,
  InstructorAssignmentsEffects,
  InstructorQNAEffects,
  InstructorReviewsEffects,
  authFeature,
  checkoutFeature,
  instructorAssignmentsFeature,
  instructorQNAFeature,
  instructorReviewsFeature,
} from './store';

import { NgCircleProgressModule } from 'ng-circle-progress';
import { CoursesFilterComponent } from './components/courses-filter/courses-filter.component';
import { GoogleSsoDirective } from './directives';
import { ScrollActionDirective } from './directives/scroll-action.directive';
import { CartEffectsV2, cartFeatureV2 } from './store/cartV2';
import { CourseDetailsEffects, courseDetailsFeature } from './store/courses';
import { PaymentEffects, paymentFeature } from './store/payment';
import { PayoutEffects, payoutFeature } from './store/payout';

const UppyModules = [
  UppyAngularDashboardModule,
  UppyAngularStatusBarModule,
  UppyAngularDragDropModule,
  UppyAngularProgressBarModule,
  UppyAngularDashboardModalModule,
];

const sharedModules = [ThkeeCommonModule];

// const sharedComponents = [];

const standaloneComponents = [
  CommonSwiperComponent,
  MainEditorComponent,
  SpinnerComponent,
  FirebaseAuthComponent,
  AdminHeaderComponent,
  AccordionComponent,
  BreadcurmbsComponent,
  CarouselNavComponent,
  AssignmentsAccordionComponent,
  CarouselComponent,
  UiSnippetsComponent,
  NavigationComponent,
  StarRatingComponent,
  ModalComponent,
  CourseCardComponent,
  CourseGridComponent,
  FilterOptionComponent,
  CoursesFilterComponent,
  FeedbackComponent,
  GoogleAuthComponent,
  FormLoginComponent,
  MessagesComponent,
  FormSignupComponent,
  FormTapPaymentComponent,
  CategoryHeaderComponent,
  CourseHeaderComponent,
  NewCourseHeaderComponent,
  StudentHeaderComponent,
  ThkVideoPlayerComponent,
  FormlyRepeatButtonComponent,
  HeroOneComponent,
  QaACardComponent,
  QaClassCardComponent,
  QaQCardComponent,
  QaTabMenuComponent,
  LearninglabArticleComponent,
  ScrollableContentComponent,
  ProgressCircleComponent,
  LearninglabAssignmentComponent,
  ModalConfirmComponent,
  LearninglabQuizComponent,
  LearninglabVideoComponent,
  LearninglabHeaderComponent,
  LearninglabSidebarComponent,
  ModalLoginComponent,
  PaginationComponent,
  ProgressBarComponent,
  CourseSidebarComponent,
  SocialShareButtonComponent,
  TabMenuComponent,
  ToastComponent,
  SvgComponent,
  ChartComponent,
  AiButtonComponent,

  // Formly
  FormlyDeleteConfirmationComponent,
  FormlyFileDashboardInfoComponent,
  FormlyFileDefaultInfoComponent,
  FormlyImgPlaceholderComponent,
  FormlyFileDragDropInfoComponent,
  FormlyFileVideoPlayerComponent,
];
const standaloneDirectives = [
  GoogleSsoDirective,
  ScrollActionDirective,
  ClickOutsideDirective,
  DefaultImageDirective,
  TooltipDirective,
];

@NgModule({
  imports: [
    CommonModule,
    RouterModule,
    ...sharedModules,
    ...standaloneComponents,
    ...standaloneDirectives,
    FormlySelectModule,
    ReactiveFormsModule,
    FormsModule,
    FormlySetup,
    ...UppyModules,
    StoreModule.forFeature('router', routerReducer),
    StoreModule.forFeature(authFeature),
    StoreModule.forFeature(categoryFeature),
    StoreModule.forFeature(topicFeature),
    // StoreModule.forFeature(cartFeature),
    StoreModule.forFeature(cartFeatureV2),
    StoreModule.forFeature(checkoutFeature),
    StoreModule.forFeature(errorFeature),
    StoreModule.forFeature(instructorAssignmentsFeature),
    StoreModule.forFeature(instructorQNAFeature),
    StoreModule.forFeature(instructorReviewsFeature),
    StoreModule.forFeature(courseDetailsFeature),
    StoreModule.forFeature(payoutFeature),
    StoreModule.forFeature(paymentFeature),
    EffectsModule.forFeature([
      ErrorEffects,
      CategoryEffects,
      TopicEffects,
      // CartEffects,
      CartEffectsV2,
      CheckoutEffects,
      InstructorAssignmentsEffects,
      InstructorQNAEffects,
      InstructorReviewsEffects,
      CourseDetailsEffects,
      PayoutEffects,
      PaymentEffects,
    ]),
    AngularSvgIconModule.forRoot(),
    MarkdownModule.forRoot(),
    NgChartsModule,
    NgCircleProgressModule.forRoot({
      radius: 100,
      outerStrokeWidth: 16,
      innerStrokeWidth: 8,
      outerStrokeColor: '#78C000',
      innerStrokeColor: '#C7E596',
      animationDuration: 300,
    }),
  ],
  declarations: [...FormlyComponents],
  exports: [
    ...sharedModules,
    ...standaloneComponents,
    ...standaloneDirectives,
    FormlyModule,
    FormsModule,
    ReactiveFormsModule,
    AngularSvgIconModule,
    MarkdownModule,
  ],
  providers: [TitleCasePipe, PricePipe],
})
export class SharedModule {}
